import React, { useState, useEffect } from 'react';
import { BarChart3, Copy, Trash2 } from 'lucide-react';

const TextStatisticsTool = () => {
  const [input, setInput] = useState('');
  const [stats, setStats] = useState(null);

  const analyzeText = (text) => {
    if (!text.trim()) {
      setStats(null);
      return;
    }

    const chars = text.length;
    const charsNoSpaces = text.replace(/\s/g, '').length;
    const words = text.trim().split(/\s+/).filter(w => w.length > 0);
    const wordCount = words.length;
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0).length;
    const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0).length;
    const lines = text.split('\n').length;

    // Word length analysis
    const wordLengths = words.map(w => w.replace(/[^a-zA-Z]/g, '').length);
    const avgWordLength = wordLengths.length > 0 
      ? (wordLengths.reduce((a, b) => a + b, 0) / wordLengths.length).toFixed(1)
      : 0;
    const longestWord = words.reduce((a, b) => 
      a.replace(/[^a-zA-Z]/g, '').length > b.replace(/[^a-zA-Z]/g, '').length ? a : b, ''
    ).replace(/[^a-zA-Z]/g, '');

    // Sentence length
    const sentenceLengths = text.split(/[.!?]+/).filter(s => s.trim()).map(s => 
      s.trim().split(/\s+/).filter(w => w.length > 0).length
    );
    const avgSentenceLength = sentenceLengths.length > 0
      ? (sentenceLengths.reduce((a, b) => a + b, 0) / sentenceLengths.length).toFixed(1)
      : 0;

    // Reading time (200 wpm reading, 150 wpm speaking)
    const readingTime = Math.ceil(wordCount / 200);
    const speakingTime = Math.ceil(wordCount / 150);

    // Character frequency
    const letterFreq = {};
    for (const char of text.toLowerCase()) {
      if (/[a-z]/.test(char)) {
        letterFreq[char] = (letterFreq[char] || 0) + 1;
      }
    }
    const sortedLetters = Object.entries(letterFreq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    // Word frequency
    const wordFreq = {};
    words.forEach(w => {
      const word = w.toLowerCase().replace(/[^a-z]/g, '');
      if (word.length > 2) {
        wordFreq[word] = (wordFreq[word] || 0) + 1;
      }
    });
    const sortedWords = Object.entries(wordFreq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    // Unique words
    const uniqueWords = new Set(words.map(w => w.toLowerCase().replace(/[^a-z]/g, '')));

    // Readability (Flesch-Kincaid approximation)
    const syllables = words.reduce((count, word) => {
      return count + countSyllables(word);
    }, 0);
    const fleschScore = wordCount > 0 && sentences > 0
      ? Math.round(206.835 - 1.015 * (wordCount / sentences) - 84.6 * (syllables / wordCount))
      : 0;

    setStats({
      chars, charsNoSpaces, wordCount, sentences, paragraphs, lines,
      avgWordLength, longestWord, avgSentenceLength,
      readingTime, speakingTime,
      sortedLetters, sortedWords,
      uniqueWords: uniqueWords.size,
      fleschScore
    });
  };

  const countSyllables = (word) => {
    word = word.toLowerCase().replace(/[^a-z]/g, '');
    if (word.length <= 3) return 1;
    word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
    word = word.replace(/^y/, '');
    const matches = word.match(/[aeiouy]{1,2}/g);
    return matches ? matches.length : 1;
  };

  useEffect(() => {
    const timer = setTimeout(() => analyzeText(input), 100);
    return () => clearTimeout(timer);
  }, [input]);

  const getReadabilityLevel = (score) => {
    if (score >= 90) return { level: 'Very Easy', color: 'text-green-400' };
    if (score >= 80) return { level: 'Easy', color: 'text-green-400' };
    if (score >= 70) return { level: 'Fairly Easy', color: 'text-blue-400' };
    if (score >= 60) return { level: 'Standard', color: 'text-blue-400' };
    if (score >= 50) return { level: 'Fairly Difficult', color: 'text-yellow-400' };
    if (score >= 30) return { level: 'Difficult', color: 'text-orange-400' };
    return { level: 'Very Difficult', color: 'text-red-400' };
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Enter Text to Analyze
        </label>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Paste or type your text here to get detailed statistics..."
          className="w-full h-48 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {stats && (
        <div className="space-y-6">
          {/* Basic Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { label: 'Characters', value: stats.chars, color: 'text-blue-400' },
              { label: 'No Spaces', value: stats.charsNoSpaces, color: 'text-blue-300' },
              { label: 'Words', value: stats.wordCount, color: 'text-green-400' },
              { label: 'Sentences', value: stats.sentences, color: 'text-purple-400' },
              { label: 'Paragraphs', value: stats.paragraphs, color: 'text-pink-400' },
              { label: 'Lines', value: stats.lines, color: 'text-yellow-400' },
            ].map((stat) => (
              <div key={stat.label} className="p-4 bg-gray-800 rounded-lg text-center">
                <div className={`text-2xl font-bold ${stat.color}`}>{stat.value.toLocaleString()}</div>
                <div className="text-xs text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Time Estimates */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="text-lg font-semibold text-white mb-1">
                {stats.readingTime} min
              </div>
              <div className="text-sm text-gray-400">Reading Time (200 wpm)</div>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="text-lg font-semibold text-white mb-1">
                {stats.speakingTime} min
              </div>
              <div className="text-sm text-gray-400">Speaking Time (150 wpm)</div>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <div className={`text-lg font-semibold mb-1 ${getReadabilityLevel(stats.fleschScore).color}`}>
                {stats.fleschScore} - {getReadabilityLevel(stats.fleschScore).level}
              </div>
              <div className="text-sm text-gray-400">Flesch Reading Ease</div>
            </div>
          </div>

          {/* Word Analysis */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Word Analysis</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Unique Words:</span>
                  <span className="text-white">{stats.uniqueWords}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Avg Word Length:</span>
                  <span className="text-white">{stats.avgWordLength} chars</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Longest Word:</span>
                  <span className="text-white">{stats.longestWord || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Avg Sentence Length:</span>
                  <span className="text-white">{stats.avgSentenceLength} words</span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Top Words</h3>
              <div className="space-y-1">
                {stats.sortedWords.slice(0, 5).map(([word, count], i) => (
                  <div key={word} className="flex justify-between text-sm">
                    <span className="text-gray-400">{i + 1}. {word}</span>
                    <span className="text-blue-400">{count}x</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Letter Frequency */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <h3 className="text-sm font-medium text-gray-300 mb-3">Letter Frequency</h3>
            <div className="flex flex-wrap gap-2">
              {stats.sortedLetters.map(([letter, count]) => (
                <div key={letter} className="px-3 py-2 bg-gray-700 rounded text-center min-w-[60px]">
                  <div className="text-lg font-bold text-blue-400">{letter.toUpperCase()}</div>
                  <div className="text-xs text-gray-500">{count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => { setInput(''); setStats(null); }}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default TextStatisticsTool;
